import { Schema } from "effect";

export const MAX_BLEND_COMPONENTS = 20;
const identifier = Schema.String.pipe(Schema.minLength(1), Schema.maxLength(100));
const basisPoints = (max: number, min = 0) =>
  Schema.Number.pipe(Schema.int(), Schema.between(min, max));
export const BlendSourceSchema = Schema.Union(
  Schema.Struct({ kind: Schema.Literal("lot"), lotId: identifier }),
  Schema.Struct({ kind: Schema.Literal("manual"), coffeeId: identifier }),
);
export const BlendRoastSettingsSchema = Schema.Struct({
  machineId: Schema.optional(identifier),
  profileVersionId: Schema.optional(identifier),
  expectedLossBps: Schema.optional(basisPoints(9999)),
});
const component = {
  source: BlendSourceSchema,
  fractionBps: basisPoints(10000, 1),
};
const components = <A, I, R>(schema: Schema.Schema<A, I, R>) =>
  Schema.Array(schema).pipe(Schema.minItems(2), Schema.maxItems(MAX_BLEND_COMPONENTS));
const unit = Schema.Literal("lb", "kg", "g");
export const BlendRecipeSchema = Schema.Union(
  Schema.Struct({
    kind: Schema.Literal("post_roast"),
    unit,
    components: components(Schema.Struct({ ...component, roast: BlendRoastSettingsSchema })),
  }),
  Schema.Struct({
    kind: Schema.Literal("pre_roast"),
    unit,
    components: components(Schema.Struct(component)),
    roast: BlendRoastSettingsSchema,
  }),
);
export type BlendRecipe = typeof BlendRecipeSchema.Type;
export type BlendSource = typeof BlendSourceSchema.Type;
export type BlendRoastSettings = typeof BlendRoastSettingsSchema.Type;
export function blendSourceKey(source: BlendSource) {
  return source.kind === "lot" ? `lot:${source.lotId}` : `manual:${source.coffeeId}`;
}
export function validateBlendRecipe(value: unknown): BlendRecipe {
  const recipe = Schema.decodeUnknownSync(BlendRecipeSchema)(value, { onExcessProperty: "error" });
  if (recipe.components.reduce((sum, item) => sum + item.fractionBps, 0) !== 10000)
    throw new Error("Coffee percentages must total exactly 100%.");
  const seen = new Set<string>();
  for (const item of recipe.components) {
    const roast = recipe.kind === "post_roast" && "roast" in item ? item.roast : recipe.kind === "pre_roast" ? recipe.roast : undefined;
    if (roast?.profileVersionId && !roast.machineId)
      throw new Error("Choose a machine before choosing its reference profile.");
    const key = JSON.stringify([blendSourceKey(item.source), roast?.machineId, roast?.profileVersionId]);
    if (seen.has(key)) throw new Error("This coffee and roast setup appears twice. Combine its percentages or choose a different profile.");
    seen.add(key);
  }
  return recipe;
}

/** Estimates never post inventory; pre-roast loss belongs to the whole blend. */
export function blendRequirements(recipe: BlendRecipe, roastedWeight: number) {
  validateBlendRecipe(recipe);
  if (!Number.isFinite(roastedWeight) || roastedWeight <= 0)
    throw new Error("Enter a positive roasted output weight.");
  return recipe.components.map((item) => {
    const roast = recipe.kind === "post_roast" && "roast" in item ? item.roast : recipe.kind === "pre_roast" ? recipe.roast : undefined;
    if (roast?.expectedLossBps === undefined)
      throw new Error(recipe.kind === "pre_roast" ? "Enter the expected loss for the blended roast." : "Enter the expected loss for each coffee.");
    const greenWeight = roastedWeight * item.fractionBps / 10000 / (1 - roast.expectedLossBps / 10000);
    if (!Number.isFinite(greenWeight)) throw new Error("The requested weight is too large to calculate. Enter a smaller output weight.");
    return {
      source: item.source,
      roast,
      greenWeight,
      // Pre-roast component output cannot be measured separately.
      roastedWeight: recipe.kind === "post_roast" ? roastedWeight * item.fractionBps / 10000 : null,
    };
  });
}
